import React, { useState, useContext } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, bdg, btnP, inpS, sp as spStyle, rad, icBox } from "../../constants/styles";
import { gradCardStyle, gradLift } from "../../constants/gradCard";
import Fld from "../common/Fld";
import Ic from "../common/Ic";
import StatusTracker from "../common/StatusTracker";
import { rankServicePersons } from "../../utils/distance";
import { applyAdvanceToDue } from "../../utils/advance";
import { formatPropAddr } from "../../utils/propertyAddr";

function MaintPage() {
  const { t, h, mts, setMts, tens, props, pays, setPays, ccs, setCcs, flash, showConfirm, addNotif, sps, go } = useContext(Ctx); const uc = { high: h.rd, med: h.wn, low: h.gn }; const sc2 = { open: h.rd, assigned: h.wn, done: h.gn };
  const [editId, sEd] = useState(null); const [eDesc, sED] = useState(""); const [eUrg, sEU] = useState(""); const [eSt, sES] = useState("");
  // Card collapse — done maintenance items render as a compact one-line
  // pill by default (same UX as the tenant TenMaintList). Open ones
  // (st !== 'done') always render fully expanded since there's still
  // work to manage. Tapping the compact pill toggles the id in this
  // Set; tapping the card chevron when expanded collapses it back.
  const [expandedDoneIds, setExpandedDoneIds] = useState(new Set());
  const toggleDoneExpanded = (id) => {
    setExpandedDoneIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const [eAsnN, sEAsnN] = useState(""); const [eAsnP, sEAsnP] = useState("");
  const [eExpAmt, sEExpAmt] = useState(""); const [eExpTarget, sEExpTarget] = useState("all"); const [eExpSelected, sEExpSelected] = useState([]);
  // Inline assign/resolve forms (replaces window.prompt)
  const [assignId, setAssignId] = useState(null); const [asnName, setAsnName] = useState(""); const [asnPhone, setAsnPhone] = useState("");
  const [resolveId, setResolveId] = useState(null); const [resNotes, setResNotes] = useState("");
  const [resAsnN, setResAsnN] = useState(""); const [resAsnP, setResAsnP] = useState("");
  // Charge target defaults to "none" so the landlord must consciously
  // pick who pays before clicking Done — prevents accidental "All
  // Tenants in Property" charges when the landlord intended to absorb
  // the cost or charge a single tenant.
  const [resExpAmt, setResExpAmt] = useState(""); const [resExpTarget, setResExpTarget] = useState("none"); const [resExpSelected, setResExpSelected] = useState([]);
  // Add new maintenance form
  const [showAdd, setShowAdd] = useState(false); const [addDesc, setAddDesc] = useState(""); const [addUrg, setAddUrg] = useState("med"); const [addTid, setAddTid] = useState("");
  const [addExpAmt, setAddExpAmt] = useState(""); const [addExpTarget, setAddExpTarget] = useState("all"); const [addExpSelected, setAddExpSelected] = useState([]);
  // SP Payment approval
  const [payingId, setPayingId] = useState(null); const [payMethod, setPayMethod] = useState("cash");
  const occTenants = tens.filter(te => props.some(p => p.tid === te.id && p.occ));
  const doAdd = () => {
    if (!addDesc) return;
    const newM = { id: `m${Date.now()}`, tid: addTid || null, desc: addDesc, urg: addUrg, st: "open", dt: new Date().toISOString().slice(0, 10), exp_amt: Number(addExpAmt) || 0, exp_target: addExpTarget, exp_selected: addExpTarget === "selected" ? addExpSelected : [], exp_applied: false };
    setMts([newM, ...mts]); api.saveMaintenance(newM);
    addNotif("landlord", null, `Created maintenance: "${addDesc}"`, "info");
    if (addTid) addNotif("tenant", addTid, `Landlord created maintenance request: "${addDesc}"`, "info");
    flash(t.added); setShowAdd(false); setAddDesc(""); setAddUrg("med"); setAddTid(""); setAddExpAmt(""); setAddExpTarget("all");
  };

  // Prefill Edit form. When the SP entered a charge but the landlord
  // hasn't set an expense yet, suggest sp_pay_amt as the expense and
  // default the target to the request's own tenant — saves them from
  // retyping data that's already on the card.
  const startEdit = (m) => {
    const suggestExpAmt = (!m.exp_amt && m.sp_pay_amt > 0) ? m.sp_pay_amt : (m.exp_amt || "");
    const suggestExpTarget = m.exp_target || (m.tid && !m.exp_amt ? m.tid : "all");
    sEd(m.id); sED(m.desc); sEU(m.urg); sES(m.st); sEAsnN(m.asn_n || ""); sEAsnP(m.asn_p || "");
    sEExpAmt(suggestExpAmt); sEExpTarget(suggestExpTarget); sEExpSelected(m.exp_selected || []);
    sESelSpId(m.sp_id || (m.asn_n ? "__manual__" : "")); setAssignId(null); setResolveId(null);
  };
  const saveEdit = () => {
    const uMaint = mts.find(x => x.id === editId);
    const realESpId = eSelSpId === "__manual__" ? null : eSelSpId;
    const updated = { ...uMaint, desc: eDesc, urg: eUrg, st: eSt, asn_n: eAsnN, asn_p: eAsnP, sp_id: realESpId || uMaint.sp_id || null, exp_amt: Number(eExpAmt) || 0, exp_target: eExpTarget, exp_selected: eExpTarget === "selected" ? eExpSelected : [] };
    setMts(mts.map(x => x.id === editId ? updated : x));
    api.saveMaintenance(updated);
    // Notify SP if edit changed them to a new registered SP (re-assignment).
    // Skip when the SP id is unchanged — editing description/urgency shouldn't re-ping them.
    if (realESpId && realESpId !== uMaint.sp_id) {
      addNotif("service", realESpId, `🔧 New job assigned: "${eDesc}" (${eUrg || "med"})`, `job:${uMaint.id}`);
    }
    flash(t.updated); sEd(null);
  };
  const deleteMaint = (m) => {
    showConfirm(t.deleteMaintConfirm, () => {
      api.deleteMaintenance(m.id);
      setMts(mts.filter(x => x.id !== m.id));
      addNotif("landlord", null, `Deleted maintenance: "${m.desc}"`, "info");
      if (m.tid) addNotif("tenant", m.tid, `Your maintenance request "${m.desc}" was deleted by landlord.`, "info");
      flash(t.deleted);
    });
  };
  const [selSpId, setSelSpId] = useState("");
  const [eSelSpId, sESelSpId] = useState(""); // SP selection for Edit form
  const doAssign = (m) => {
    const realSpId = selSpId === "__manual__" ? null : selSpId;
    const up = { ...m, st: "assigned", asn_n: asnName || "", asn_p: asnPhone || "", sp_id: realSpId || null, sp_status: realSpId ? "pending" : m.sp_status };
    setMts(mts.map(x => x.id === m.id ? up : x));
    api.saveMaintenance(up);
    addNotif("landlord", null, `Assigned "${m.desc}" to ${asnName || "worker"}`, "info");
    if (m.tid) addNotif("tenant", m.tid, `Your request "${m.desc}" has been assigned to ${asnName || "a worker"}.`, "info");
    // Notify the registered service person so their bell/toast fires on next sync.
    // Only applicable when a real SP (not manual) was selected — manual entries have no account.
    // Encode the maint id into `type` ("job:<id>") so clicking the notification deep-links
    // to the specific job detail page — see TopBar's getTarget().
    if (realSpId) addNotif("service", realSpId, `🔧 New job assigned: "${m.desc}" (${m.urg || "med"})`, `job:${m.id}`);
    flash(t.assigned + "!"); setAssignId(null); setAsnName(""); setAsnPhone(""); setSelSpId("");
  };
  const doResolve = (m) => {
    const expAmt = Number(resExpAmt) || 0;
    const expTarget = resExpTarget;
    // A charge target is only required when there IS an expense to bill.
    // Zero-cost fixes (or costs the landlord absorbs by leaving the
    // amount empty) can be resolved without charging anyone — the old
    // gate made those tickets impossible to close from this page.
    if (expAmt > 0 && (!expTarget || expTarget === "none")) {
      flash(t.chargeTargetRequired || "Please choose who to charge before marking Done", "error");
      return;
    }
    const shouldApplyExp = expAmt > 0 && expTarget && expTarget !== "none" && !m.exp_applied;
    const up = { ...m, st: "done", asn_n: resAsnN || "", asn_p: resAsnP || "", asn_notes: resNotes || "", exp_amt: expAmt, exp_target: expAmt > 0 ? expTarget : "none", exp_applied: shouldApplyExp ? true : (m.exp_applied || false) };
    setMts(mts.map(x => x.id === m.id ? up : x));
    api.saveMaintenance(up);

    // Create immediate separate payment for maintenance expense (not bundled with rent)
    if (shouldApplyExp) {
      let targetIds = [];
      if (expTarget === "all") {
        const maintProp = m.tid ? tens.find(te => te.id === m.tid)?.pid : null;
        if (maintProp) {
          targetIds = tens.filter(te => !te.vac && te.pid === maintProp).map(te => te.id);
        } else {
          targetIds = tens.filter(te => !te.vac && te.pid && props.find(p => p.id === te.pid)).map(te => te.id);
        }
      } else {
        targetIds = [expTarget];
      }
      if (targetIds.length > 0) {
        const splitAmt = Math.round(expAmt / targetIds.length);
        // Thread `workingPays` across iterations so advance consumed on one
        // tenant is not re-offered to the next (matters when the same tenant
        // appears twice, or for consistency within a batch).
        let workingPays = [...pays];
        const appendedPays = [];
        for (const tid of targetIds) {
          const payId = `pay_maint_${m.id}_${tid}_${Date.now()}`;
          const basePay = { id: payId, tid, amt: 0, com: splitAmt, mo: `🔧 ${m.desc}`, mth: null, st: "due", dt: null, advance: 0, txns: [] };
          const { newPay: finalPay, updatedPays, consumed } = applyAdvanceToDue(basePay, workingPays, tid);
          // Merge advance-reduced source records into our working copy.
          workingPays = workingPays.map(x => updatedPays.find(u => u.id === x.id) || x);
          workingPays.push(finalPay);
          appendedPays.push(finalPay);
          api.savePayment(finalPay);
          updatedPays.forEach(u => api.savePayment(u));
          if (consumed >= splitAmt) {
            addNotif("tenant", tid, `🔧 "${m.desc}" charge of ₹${splitAmt.toLocaleString()} auto-paid from your advance credit ✓`, "payment");
          } else if (consumed > 0) {
            addNotif("tenant", tid, `🔧 "${m.desc}" ₹${splitAmt.toLocaleString()}: ₹${consumed.toLocaleString()} applied from advance, ₹${(splitAmt - consumed).toLocaleString()} remaining.`, "payment");
          } else {
            addNotif("tenant", tid, `🔧 Maintenance charge: ₹${splitAmt.toLocaleString()} for "${m.desc}". Please pay.`, "payment");
          }
        }
        setPays(workingPays);
      }
    }

    addNotif("landlord", null, `Resolved maintenance: "${m.desc}"`, "info");
    if (m.tid) addNotif("tenant", m.tid, `Your request "${m.desc}" has been resolved.`, "info");
    flash(t.done + "!"); setResolveId(null); setResNotes(""); setResAsnN(""); setResAsnP(""); setResExpAmt(""); setResExpTarget("none");
  };

  const applyExpense = (m) => {
    const amt = m.exp_amt;
    if (!amt) return;
    const d = new Date();
    const mNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const moStr = `${mNames[d.getMonth()]} ${d.getFullYear().toString().slice(-2)}`;

    // Determine which tenants to charge
    let targetIds = [];
    if (m.exp_target === "all") {
      const maintProp = m.tid ? tens.find(te => te.id === m.tid)?.pid : null;
      if (maintProp) {
        targetIds = tens.filter(te => !te.vac && te.pid === maintProp).map(te => te.id);
      } else {
        targetIds = tens.filter(te => !te.vac && te.pid && props.find(p => p.id === te.pid)).map(te => te.id);
      }
    } else if (m.exp_target === "selected" && m.exp_selected?.length > 0) {
      targetIds = m.exp_selected;
    } else {
      targetIds = [m.exp_target];
    }

    if (targetIds.length === 0) { flash("No tenants to charge", "error"); return; }

    // Split amount equally among target tenants
    const splitAmt = Math.round(amt / targetIds.length);

    // Create a SEPARATE immediate "due" payment for each target tenant (not bundled with rent).
    // Auto-apply any tenant advance credit before leaving the charge as "due".
    let workingPays = [...pays];
    for (const tid of targetIds) {
      const payId = `pay_maint_${m.id}_${tid}_${Date.now()}`;
      const basePay = {
        id: payId, tid, amt: 0, com: splitAmt,
        mo: `🔧 ${m.desc}`, mth: null, st: "due",
        dt: null, advance: 0, txns: []
      };
      const { newPay: finalPay, updatedPays, consumed } = applyAdvanceToDue(basePay, workingPays, tid);
      workingPays = workingPays.map(x => updatedPays.find(u => u.id === x.id) || x);
      workingPays.push(finalPay);
      api.savePayment(finalPay);
      updatedPays.forEach(u => api.savePayment(u));
      if (consumed >= splitAmt) {
        addNotif("tenant", tid, `🔧 "${m.desc}" charge of ₹${splitAmt.toLocaleString()} auto-paid from your advance credit ✓`, "payment");
      } else if (consumed > 0) {
        addNotif("tenant", tid, `🔧 "${m.desc}" ₹${splitAmt.toLocaleString()}: ₹${consumed.toLocaleString()} applied from advance, ₹${(splitAmt - consumed).toLocaleString()} remaining.`, "payment");
      } else {
        addNotif("tenant", tid, `🔧 Maintenance charge: ₹${splitAmt.toLocaleString()} for "${m.desc}". Please pay.`, "payment");
      }
    }
    setPays(workingPays);

    const updated = { ...m, exp_applied: true };
    setMts(mts.map(x => x.id === m.id ? updated : x));
    api.saveMaintenance(updated);
    flash(t.expApplied);
  };

  // One-tap "charge SP amount to tenant". Wraps applyExpense so we
  // share its idempotency, advance-credit handling, and notification
  // logic instead of re-implementing the payment plumbing.
  //   target = "all"        → split across all occupied tenants of the property
  //   target = <tid>        → charge that single tenant
  // Guard: applyExpense doesn't self-check exp_applied, so we do here
  // to prevent duplicate payment rows if the user double-taps.
  const chargeSpAmtTo = (m, target) => {
    if (m.exp_applied) { flash(t.expAlreadyApplied || "Already charged", "error"); return; }
    if (!m.sp_pay_amt || m.sp_pay_amt <= 0) return;
    applyExpense({ ...m, exp_amt: m.sp_pay_amt, exp_target: target, exp_selected: [] });
  };

  const approveSpPay = (m) => {
    const up = { ...m, sp_pay_st: "paid" };
    setMts(mts.map(x => x.id === m.id ? up : x));
    api.saveMaintenance(up);
    // Record in service_payments table
    api.saveServicePayment({ id: `sp_pay_${Date.now()}`, sp_id: m.sp_id, maint_id: m.id, amount: m.sp_pay_amt, st: "paid", dt: new Date().toISOString().slice(0, 10), notes: `${m.desc} — ${payMethod}` });
    addNotif("landlord", null, `Approved ₹${m.sp_pay_amt.toLocaleString()} payment to ${m.asn_n || "service person"}`, "payment");
    flash(`✅ ₹${m.sp_pay_amt.toLocaleString()} payment approved!`);
    setPayingId(null); setPayMethod("cash");
  };

  return <div style={{ paddingTop: 16 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
      <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: 0 }}>{t.maintenance}</h2>
      <button data-coach="maint-add" onClick={() => setShowAdd(!showAdd)} style={{ background: h.ac, border: "none", borderRadius: 10, padding: "8px 14px", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{showAdd ? "✕ " + t.cancel : "+ " + t.add}</button>
    </div>
    {showAdd && <div style={{ ...crd(h), padding: 20, marginBottom: 12 }}>
      <label style={{ color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6 }}>{t.tenOptional}</label>
      <select value={addTid} onChange={e => setAddTid(e.target.value)} style={{ ...inpS(h), marginBottom: 12 }}>
        <option value="">{t.generalNoTen}</option>
        {occTenants.map(te => <option key={te.id} value={te.id}>{te.name}</option>)}
      </select>
      <Fld l={t.desc} v={addDesc} set={setAddDesc} h={h} ph="e.g. Leaking pipe in bathroom" />
      <label style={{ color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6 }}>{t.urgency}</label>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>{["low", "med", "high"].map(u => <button key={u} onClick={() => setAddUrg(u)} style={{ flex: 1, padding: 8, borderRadius: 10, border: `1px solid ${addUrg === u ? h.ac : h.bd}`, background: addUrg === u ? `${h.ac}15` : "transparent", color: addUrg === u ? h.ac : h.sc, cursor: "pointer", fontSize: 12 }}>{u === "low" ? t.low : u === "med" ? t.med : t.high}</button>)}</div>
      <Fld l={t.expAmt} v={addExpAmt} set={setAddExpAmt} h={h} type="number" ph="₹0" />
      <label style={{ color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6 }}>{t.expTarget || "Charge To"}</label>
      <select value={addExpTarget} onChange={e => { setAddExpTarget(e.target.value); if (e.target.value !== "selected") setAddExpSelected([]); }} style={{ ...inpS(h), marginBottom: 8 }}>
        <option value="all">{t.allInProperty || "All Tenants in Property"}</option>
        <option value="selected">{t.selectTenants || "Select Tenants..."}</option>
        {occTenants.map(te => <option key={te.id} value={te.id}>{te.name} ({t.only || "only"})</option>)}
      </select>
      {addExpTarget === "selected" && <div style={{ background: h.ib, borderRadius: 10, padding: 10, marginBottom: 8, border: `1px solid ${h.bd}` }}>
        {occTenants.map(te => <label key={te.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", cursor: "pointer", borderBottom: `1px solid ${h.bd}` }}>
          <input type="checkbox" checked={addExpSelected.includes(te.id)} onChange={e => setAddExpSelected(prev => e.target.checked ? [...prev, te.id] : prev.filter(x => x !== te.id))} />
          <span style={{ color: h.tx, fontSize: 13 }}>{te.name}</span>
          <span style={{ color: h.ts, fontSize: 11, marginLeft: "auto" }}>{formatPropAddr(props.find(p => p.id === te.pid)).substring(0, 20)}</span>
        </label>)}
        {addExpSelected.length > 0 && Number(addExpAmt) > 0 && <div style={{ color: h.ac, fontSize: 11, fontWeight: 600, marginTop: 6 }}>{"\u20B9"}{Math.round(Number(addExpAmt) / addExpSelected.length).toLocaleString()} each ({addExpSelected.length} tenants)</div>}
      </div>}
      {addExpTarget === "all" && Number(addExpAmt) > 0 && occTenants.length > 0 && <div style={{ color: h.ac, fontSize: 11, fontWeight: 600, marginBottom: 8 }}>{"\u20B9"}{Math.round(Number(addExpAmt) / occTenants.length).toLocaleString()} each ({occTenants.length} tenants)</div>}
      <button onClick={doAdd} style={btnP(h)}>{t.submit}</button>
    </div>}
    <div data-coach="maint-list">
    {[...mts].sort((a, b) => (b.dt || b.id || "").localeCompare(a.dt || a.id || "")).map(m => {
      const te = tens.find(x => x.id === m.tid);
      if (editId === m.id) return <div key={m.id} style={{ ...crd(h), padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}><div style={{ color: h.ac, fontWeight: 600, fontSize: 14 }}>{t.editRequest}</div><button onClick={() => sEd(null)} style={{ background: "none", border: "none", color: h.sc, cursor: "pointer" }}>✕</button></div>
        <Fld l={t.desc} v={eDesc} set={sED} h={h} />
        <label style={{ color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6 }}>{t.urgency}</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>{["low", "med", "high"].map(u => <button key={u} onClick={() => sEU(u)} style={{ flex: 1, padding: 8, borderRadius: 10, border: `1px solid ${eUrg === u ? h.ac : h.bd}`, background: eUrg === u ? `${h.ac}15` : "transparent", color: eUrg === u ? h.ac : h.sc, cursor: "pointer", fontSize: 12 }}>{u === "low" ? t.low : u === "med" ? t.med : t.high}</button>)}</div>

        {eSt === "assigned" && (() => {
          const eMaint = mts.find(x => x.id === editId);
          const eTenant = tens.find(te2 => te2.id === eMaint?.tid);
          const eProp = props.find(p => p.id === eTenant?.pid) || props.find(p => p.tid === eMaint?.tid) || null;
          const eRanked = rankServicePersons(sps, eProp, eDesc);
          const eShowManual = eSelSpId === "__manual__";
          return <div style={{ background: `${h.wn}08`, borderRadius: rad.md, padding: spStyle.md, marginBottom: 8, border: `1px solid ${h.wn}30` }}>
            <div style={{ color: h.wn, fontWeight: 600, fontSize: 12, marginBottom: spStyle.sm }}>{t.assignTo || "Assign To"}</div>
            {eRanked.length > 0 && !eShowManual && <div style={{ marginBottom: spStyle.md }}>
              <div style={{ color: h.ts, fontSize: 10, fontWeight: 600, textTransform: "uppercase", marginBottom: spStyle.xs }}>{t.suggestedPersons || "Suggested"}</div>
              {eRanked.map(s => {
                const activeJobs2 = mts.filter(mt => mt.sp_id === s.id && mt.st !== "done").length;
                const sel2 = eSelSpId === s.id;
                return <div key={s.id} onClick={() => { sESelSpId(s.id); sEAsnN(s.name); sEAsnP(s.ph); }}
                  style={{ display: "flex", alignItems: "center", gap: spStyle.sm, padding: `${spStyle.sm}px ${spStyle.md}px`, borderRadius: rad.sm, border: `1px solid ${sel2 ? h.ac : h.bd}`, background: sel2 ? `${h.ac}08` : h.cd, marginBottom: spStyle.xs, cursor: "pointer", transition: "all 0.15s" }}>
                  <div style={icBox(s.matchScore >= 2 ? h.gn : h.ts, 32)}>
                    <Ic n="wrench" s={14} c={s.matchScore >= 2 ? h.gn : h.ts} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{s.name}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 2 }}>
                      <span style={{ ...bdg(`${h.ac}15`, h.ac), fontSize: 9 }}>{s.skill || "General"}</span>
                      {s.distance != null && <span style={{ ...bdg(`${h.gn}15`, h.gn), fontSize: 9 }}>{s.distance < 1 ? `${Math.round(s.distance * 1000)}m` : `${s.distance.toFixed(1)}km`}</span>}
                      {s.distance == null && <span style={{ ...bdg(`${h.tt}15`, h.tt), fontSize: 9 }}>{t.noLocation || "No location"}</span>}
                      {activeJobs2 > 0 && <span style={{ ...bdg(`${h.wn}15`, h.wn), fontSize: 9 }}>{activeJobs2} {t.activeJobs || "active"}</span>}
                    </div>
                  </div>
                  {sel2 && <Ic n="check" s={16} c={h.ac} />}
                </div>;
              })}
            </div>}
            {!eShowManual && <button onClick={() => { sESelSpId("__manual__"); sEAsnN(""); sEAsnP(""); }} style={{ display: "flex", alignItems: "center", gap: spStyle.xs, background: "none", border: `1px dashed ${h.bd}`, borderRadius: rad.sm, padding: `${spStyle.sm}px ${spStyle.md}px`, color: h.ts, fontSize: 12, cursor: "pointer", width: "100%", marginBottom: spStyle.sm }}>
              <Ic n="plus" s={14} c={h.ts} /> {t.manualEntry || "Enter manually"}
            </button>}
            {eShowManual && <>
              <Fld l={t.assigneeName} v={eAsnN} set={sEAsnN} h={h} ph="e.g. Raj Plumber" />
              <Fld l={t.assigneePhone} v={eAsnP} set={sEAsnP} h={h} ph="+91 98765..." />
            </>}
          </div>;
        })()}

        <Fld l={t.expAmt} v={eExpAmt} set={sEExpAmt} h={h} type="number" ph="₹0" />
        <label style={{ color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6 }}>{t.expTarget}</label>
        <select value={eExpTarget} onChange={e => { sEExpTarget(e.target.value); if (e.target.value !== "selected") sEExpSelected([]); }} style={{ ...inpS(h), marginBottom: 8 }}>
          <option value="all">{t.allInProperty || "All Tenants in Property"}</option>
          <option value="selected">{t.selectTenants || "Select Tenants..."}</option>
          {occTenants.map(te2 => <option key={te2.id} value={te2.id}>{te2.name} ({t.only || "only"})</option>)}
        </select>
        {eExpTarget === "selected" && <div style={{ background: h.ib, borderRadius: 10, padding: 10, marginBottom: 8, border: `1px solid ${h.bd}` }}>
          {occTenants.map(te2 => <label key={te2.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", cursor: "pointer", borderBottom: `1px solid ${h.bd}` }}>
            <input type="checkbox" checked={eExpSelected.includes(te2.id)} onChange={e => sEExpSelected(prev => e.target.checked ? [...prev, te2.id] : prev.filter(x => x !== te2.id))} />
            <span style={{ color: h.tx, fontSize: 13 }}>{te2.name}</span>
            <span style={{ color: h.ts, fontSize: 11, marginLeft: "auto" }}>{formatPropAddr(props.find(p => p.id === te2.pid)).substring(0, 20)}</span>
          </label>)}
          {eExpSelected.length > 0 && Number(eExpAmt) > 0 && <div style={{ color: h.ac, fontSize: 11, fontWeight: 600, marginTop: 6 }}>{"\u20B9"}{Math.round(Number(eExpAmt) / eExpSelected.length).toLocaleString()} each ({eExpSelected.length} tenants)</div>}
        </div>}
        {eExpTarget === "all" && Number(eExpAmt) > 0 && occTenants.length > 0 && <div style={{ color: h.ac, fontSize: 11, fontWeight: 600, marginBottom: 8 }}>{"\u20B9"}{Math.round(Number(eExpAmt) / occTenants.length).toLocaleString()} each ({occTenants.length} tenants)</div>}

        <label style={{ color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6 }}>{t.status}</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>{["open", "assigned", "done"].map(s => <button key={s} onClick={() => {
          if (s === "done") {
            // Save edit first, then open resolve form pre-filled
            const uMaint = mts.find(x => x.id === editId);
            const updated = { ...uMaint, desc: eDesc, urg: eUrg, st: uMaint.st, asn_n: eAsnN, asn_p: eAsnP, exp_amt: Number(eExpAmt) || 0, exp_target: eExpTarget };
            setMts(mts.map(x => x.id === editId ? updated : x)); api.saveMaintenance(updated);
            sEd(null); setResolveId(editId); setResAsnN(eAsnN); setResAsnP(eAsnP); setResExpAmt(eExpAmt); setResExpTarget(eExpTarget); setResNotes(""); setAssignId(null);
          } else sES(s);
        }} style={{ flex: 1, padding: 8, borderRadius: 10, border: `1px solid ${eSt === s ? sc2[s] : h.bd}`, background: eSt === s ? `${sc2[s]}20` : "transparent", color: eSt === s ? sc2[s] : h.sc, cursor: "pointer", fontSize: 12 }}>{s === "open" ? t.open : s === "assigned" ? t.assigned : t.done}</button>)}</div>
        <button onClick={saveEdit} style={btnP(h)}>{t.save}</button></div>;

      // ── Done + collapsed → compact one-line summary ───────────────
      // Done maintenance items default to compact because the landlord
      // mostly cares about the open queue. Each finished job is a row:
      //   ✓ icon | Title | Resolved · date | ▾ (tap to expand)
      // Tapping anywhere on the row toggles into the full card so the
      // landlord can see assignee, expense, SP payment status, etc.
      const isDone = m.st === "done";
      const isExpanded = !isDone || expandedDoneIds.has(m.id);
      if (isDone && !isExpanded) {
        const doneDate = m.confirmed_at || m.dt || "";
        const formattedDate = doneDate ? (() => {
          try {
            const d = new Date(doneDate);
            if (Number.isNaN(d.getTime())) return doneDate;
            return d.toLocaleString('en-IN', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
          } catch { return doneDate; }
        })() : "";
        return (
          <div key={m.id} onClick={() => toggleDoneExpanded(m.id)}
            onMouseOver={(e) => gradLift(e, "green", true)}
            onMouseOut={(e) => gradLift(e, "green", false)}
            style={{
            ...crd(h),
            ...gradCardStyle("green"),
            padding: "12px 14px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 28, height: 28, borderRadius: "50%",
              background: "rgba(255,255,255,0.22)", flexShrink: 0,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "#fff", fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.desc}</div>
              <div style={{ color: "#fff", fontSize: 11, fontWeight: 600, marginTop: 2 }}>
                {t.done || "Resolved"}
                <span style={{ color: "rgba(255,255,255,0.8)", fontWeight: 400 }}>
                  {te?.name ? ` · ${te.name}` : ""}{formattedDate ? ` · ${formattedDate}` : ""}
                </span>
              </div>
            </div>
            <span style={{ color: "#fff", fontSize: 16, lineHeight: 1 }}>{"▾"}</span>
          </div>
        );
      }

      // Click anywhere on a DONE-and-expanded card to collapse it back.
      // We attach the handler at the card root and skip the toggle when
      // the click landed on an interactive element (button, input,
      // textarea, select, anchor, label) via closest() — that way the
      // user can still tap Edit / Delete / Assign / Pay buttons without
      // accidentally collapsing the card. Open tickets don't collapse,
      // so no handler is attached for those.
      const onCardClick = isDone
        ? (e) => {
            const interactive = e.target.closest('button, a, input, textarea, select, label');
            if (interactive) return;
            toggleDoneExpanded(m.id);
          }
        : undefined;
      return <div key={m.id} onClick={onCardClick} style={{ ...crd(h), cursor: isDone ? "pointer" : "default" }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}><div style={{ flex: 1 }}><div style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>{m.desc}</div><div style={{ color: h.sc, fontSize: 11, marginTop: 2 }}>{te?.name} · {m.dt}</div></div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}><span style={bdg(`${uc[m.urg]}20`, uc[m.urg])}>{m.urg === "high" ? t.high : m.urg === "med" ? t.med : t.low}</span><span style={bdg(`${sc2[m.st]}20`, sc2[m.st])}>{m.st === "open" ? t.open : m.st === "done" ? t.done : m.sp_status === "accepted" ? (t.accepted || "Accepted") : m.sp_status === "in_progress" ? (t.inProgress || "In Progress") : m.sp_status === "completed" ? (t.completed || "Completed") : t.assigned}</span>
            {/* When the card is done AND expanded, surface a minimize
                affordance in the header so the landlord can collapse
                it back without scrolling to find a button. Open
                tickets never collapse — they always need attention. */}
            {isDone && (
              <button onClick={(e) => { e.stopPropagation(); toggleDoneExpanded(m.id); }} style={{
                background: "none", border: "none", color: h.sc,
                cursor: "pointer", fontSize: 16, padding: 0, lineHeight: 1, marginTop: 2,
              }} title="Collapse">{"▴"}</button>
            )}
          </div></div>

        {/* Five-step progress tracker — same component the tenant sees.
            Visible on every card so the landlord and tenant view the
            SAME job lifecycle in the SAME visual treatment. */}
        <StatusTracker st={m.st} sp_status={m.sp_status} h={h} />

        {m.st === "assigned" && m.asn_n && <div style={{ background: `${h.wn}10`, borderRadius: 8, padding: 8, marginTop: 8, border: `1px solid ${h.wn}40` }}>
          <div style={{ color: h.wn, fontSize: 10, fontWeight: 600, textTransform: "uppercase", marginBottom: 2 }}>{t.assignedTo}</div>
          <div style={{ color: h.tx, fontSize: 12, fontWeight: 600 }}>🛠️ {m.asn_n} <span style={{ color: h.sc, fontWeight: 400 }}>({m.asn_p || t.noPhoneShort})</span></div>
          {m.sp_status === 'completed' && !m.confirmed_by_tenant && <>
            {m.sp_pay_amt > 0 && <div style={{ marginTop: 6, color: h.tx, fontSize: 12 }}>SP Charges: <span style={{ fontWeight: 700 }}>₹{m.sp_pay_amt.toLocaleString()}</span></div>}
            {m.sp_pay_amt > 0 && <>
              <div style={{ color: h.sc, fontSize: 10, marginTop: 6 }}>How did you pay SP?</div>
              <div style={{ display: "flex", gap: 4, marginTop: 4 }}>{["cash", "upi", "bank"].map(m2 => <button key={m2} onClick={() => setPayMethod(m2)} style={{ flex: 1, padding: 5, borderRadius: 8, border: `1px solid ${payMethod === m2 ? h.ac : h.bd}`, background: payMethod === m2 ? `${h.ac}15` : "transparent", color: payMethod === m2 ? h.ac : h.sc, cursor: "pointer", fontSize: 10, fontWeight: payMethod === m2 ? 600 : 400 }}>{m2 === "cash" ? "💵 Cash" : m2 === "upi" ? "📱 UPI" : "🏦 Bank"}</button>)}</div>
            </>}
            {/* Charge to tenant option */}
            {!m.exp_applied && m.tid && <div style={{ marginTop: 8, padding: 8, background: `${h.wn}08`, borderRadius: 8, border: `1px solid ${h.wn}25` }}>
              <div style={{ color: h.wn, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Charge to Tenant?</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ color: h.sc, fontSize: 12 }}>₹</span>
                <input type="number" placeholder={m.sp_pay_amt > 0 ? String(m.sp_pay_amt) : "0"} value={payingId === m.id ? (eExpAmt || "") : ""} onChange={e => { setPayingId(m.id); sEExpAmt(e.target.value); }} style={{ flex: 1, padding: "6px 10px", borderRadius: 8, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, fontSize: 13, fontWeight: 600, outline: "none" }} />
                <span style={{ color: h.sc, fontSize: 10 }}>→ {tens.find(x => x.id === m.tid)?.name || "Tenant"}</span>
              </div>
            </div>}
            <button onClick={async () => {
              // One click: confirm work + approve SP payment + charge tenant (if set)
              const tenantExpAmt = payingId === m.id ? (Number(eExpAmt) || 0) : 0;
              const up = { ...m, confirmed_by_tenant: true, confirmed_at: new Date().toISOString(), st: 'done', sp_pay_st: m.sp_pay_amt > 0 ? 'paid' : (m.sp_pay_st || 'pending'), exp_amt: tenantExpAmt, exp_target: m.tid || m.exp_target, exp_applied: tenantExpAmt > 0 };
              setMts(mts.map(x => x.id === m.id ? up : x));
              api.saveMaintenance(up);
              if (m.sp_pay_amt > 0 && m.sp_id) {
                api.saveServicePayment({ id: `sp_pay_${Date.now()}`, sp_id: m.sp_id, maint_id: m.id, amount: m.sp_pay_amt, st: "paid", dt: new Date().toISOString().slice(0, 10), notes: `${m.desc} — ${payMethod}` });
                addNotif("landlord", null, `Approved ₹${m.sp_pay_amt.toLocaleString()} to ${m.asn_n || "SP"}`, "payment");
              }
              // Create immediate maintenance payment for tenant,
              // auto-consuming advance credit if any is available.
              if (tenantExpAmt > 0 && m.tid) {
                const payId = `pay_maint_${m.id}_${m.tid}_${Date.now()}`;
                const basePay = { id: payId, tid: m.tid, amt: 0, com: tenantExpAmt, mo: `🔧 ${m.desc}`, mth: null, st: "due", dt: null, advance: 0, txns: [] };
                const { newPay: finalPay, updatedPays, consumed } = applyAdvanceToDue(basePay, pays, m.tid);
                setPays(prev => {
                  const merged = prev.map(x => updatedPays.find(u => u.id === x.id) || x);
                  return [...merged, finalPay];
                });
                api.savePayment(finalPay);
                updatedPays.forEach(u => api.savePayment(u));
                if (consumed >= tenantExpAmt) {
                  addNotif("tenant", m.tid, `🔧 "${m.desc}" charge of ₹${tenantExpAmt.toLocaleString()} auto-paid from your advance credit ✓`, "payment");
                } else if (consumed > 0) {
                  addNotif("tenant", m.tid, `🔧 "${m.desc}" ₹${tenantExpAmt.toLocaleString()}: ₹${consumed.toLocaleString()} applied from advance, ₹${(tenantExpAmt - consumed).toLocaleString()} remaining.`, "payment");
                } else {
                  addNotif("tenant", m.tid, `🔧 Maintenance charge: ₹${tenantExpAmt.toLocaleString()} for "${m.desc}". Please pay.`, "payment");
                }
              }
              flash(m.sp_pay_amt > 0 ? `✅ Confirmed & Paid ₹${m.sp_pay_amt.toLocaleString()}!${tenantExpAmt > 0 ? ` ₹${tenantExpAmt} charged to tenant.` : ""}` : "✅ Work confirmed!");
              setPayingId(null); sEExpAmt("");
            }} style={{ marginTop: 6, width: "100%", padding: 10, borderRadius: 8, border: "none", background: h.gn, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>
              {m.sp_pay_amt > 0 ? `✅ Confirm & Pay ₹${m.sp_pay_amt.toLocaleString()}` : "✅ Confirm Work Done"}
            </button>
          </>}
          {m.confirmed_by_tenant && <div style={{ marginTop: 4, color: h.gn, fontSize: 11, fontWeight: 600 }}>✓ Work confirmed</div>}
        </div>}

        {m.st === "done" && <div style={{ background: `${h.gn}10`, borderRadius: 8, padding: 10, marginTop: 8, border: `1px solid ${h.gn}40` }}>
          <div style={{ color: h.gn, fontSize: 10, fontWeight: 600, textTransform: "uppercase", marginBottom: 4 }}>{t.done}</div>
          {m.asn_n && <div style={{ color: h.tx, fontSize: 12, fontWeight: 600, marginBottom: 2 }}>🛠️ {m.asn_n} {m.asn_p ? <span style={{ color: h.sc, fontWeight: 400 }}>({m.asn_p})</span> : ""}</div>}
          {m.exp_amt > 0 && <div style={{ color: h.tx, fontSize: 12, marginBottom: 2 }}>{t.expenseInfo || "Expense"}: <span style={{ fontWeight: 700 }}>{"\u20B9"}{m.exp_amt.toLocaleString()}</span> {"\u2192"} {m.exp_target === "all" ? <span style={{ color: h.ac }}>{t.allInProperty || "All in Property"}</span> : m.exp_target === "selected" ? <span style={{ color: h.pp || h.ac }}>{(m.exp_selected || []).length} {t.tenants || "tenants"}</span> : <span style={{ color: h.wn }}>{tens.find(x => x.id === m.exp_target)?.name || "?"}</span>} {m.exp_applied && <span style={{ color: h.gn, fontSize: 10 }}>{"\u2713"}</span>}</div>}
          {/* Tenant settlement status for the charged expense — answers
              "has the tenant paid me for this?" right on the ticket. The
              🔧 rows are stamped pay_maint_<maintId>_<tenantId>_… */}
          {m.exp_amt > 0 && m.exp_applied && (() => {
            const rows = pays.filter(p => typeof p.id === "string" && p.id.startsWith(`pay_maint_${m.id}_`));
            if (rows.length === 0) return null;
            const paidN = rows.filter(p => p.st === "paid").length;
            const pendingN = rows.filter(p => p.st !== "paid" && p.pending).length;
            const allPaid = paidN === rows.length;
            const c = allPaid ? h.gn : pendingN > 0 ? h.wn : h.rd;
            const label = allPaid
              ? (rows.length > 1 ? `✓ Paid by all ${rows.length} tenants` : "✓ Tenant paid this charge")
              : `${paidN}/${rows.length} paid${pendingN > 0 ? ` · ${pendingN} awaiting approval` : ""}`;
            return <div style={{ display: "inline-flex", alignItems: "center", padding: "4px 10px", borderRadius: 999, background: `${c}12`, color: c, fontSize: 11, fontWeight: 700, marginTop: 4 }}>{label}</div>;
          })()}
          {m.sp_notes && <div style={{ color: h.tx, fontSize: 12, marginTop: 4, padding: "4px 0", borderTop: `1px solid ${h.gn}30` }}><span style={{ fontWeight: 600 }}>{t.workNotes || "Work Notes"}:</span> {m.sp_notes}</div>}
          {m.asn_notes && <div style={{ color: h.tx, fontSize: 12, marginTop: 4, padding: "4px 0", borderTop: `1px solid ${h.gn}30` }}>{m.asn_notes}</div>}
          {/* SP Payment section */}
          {m.sp_pay_amt > 0 && <div style={{ marginTop: 6, padding: "6px 0", borderTop: `1px solid ${h.gn}30`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <span style={{ color: h.tx, fontSize: 12 }}>SP Pay: <span style={{ fontWeight: 700 }}>₹{m.sp_pay_amt.toLocaleString()}</span></span>
            </div>
            <span style={bdg(m.sp_pay_st === "paid" ? `${h.gn}20` : `${h.wn}20`, m.sp_pay_st === "paid" ? h.gn : h.wn)}>{m.sp_pay_st === "paid" ? "✅ Paid" : "⏳ Pending"}</span>
          </div>}
          {m.sp_pay_amt > 0 && m.sp_pay_st !== "paid" && <>
            <div style={{ color: h.sc, fontSize: 10, marginTop: 6 }}>How did you pay?</div>
            <div style={{ display: "flex", gap: 4, marginTop: 4 }}>{["cash", "upi", "bank"].map(m2 => <button key={m2} onClick={() => setPayMethod(m2)} style={{ flex: 1, padding: 5, borderRadius: 8, border: `1px solid ${payMethod === m2 ? h.ac : h.bd}`, background: payMethod === m2 ? `${h.ac}15` : "transparent", color: payMethod === m2 ? h.ac : h.sc, cursor: "pointer", fontSize: 10, fontWeight: payMethod === m2 ? 600 : 400 }}>{m2 === "cash" ? "💵 Cash" : m2 === "upi" ? "📱 UPI" : "🏦 Bank"}</button>)}</div>
            <button onClick={() => approveSpPay(m)} style={{ marginTop: 6, width: "100%", padding: 10, borderRadius: 8, border: "none", background: h.gn, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>{"✅ Mark as Paid ₹" + m.sp_pay_amt.toLocaleString()}</button>
          </>}

          {/* Inline "charge tenant" action — shown when SP charge exists
              but the landlord hasn't pushed it through to the tenant yet.
              Lets them apply in one tap instead of opening Edit and
              retyping the same amount. The Edit "Custom..." path stays
              available for split / partial-amount cases. */}
          {m.sp_pay_amt > 0 && !m.exp_applied && (() => {
            const reqTen = m.tid ? tens.find(x => x.id === m.tid) : null;
            return <div style={{
              marginTop: 8, padding: 10, borderRadius: 8,
              background: `${h.wn}10`, border: `1px dashed ${h.wn}80`,
            }}>
              <div style={{ color: h.wn, fontSize: 11, fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                <span>{"⚠"}</span>
                <span>{t.notYetCharged || "Tenant not charged yet"}</span>
              </div>
              <div style={{ color: h.sc, fontSize: 10, marginBottom: 8 }}>
                {t.chargeSpHint || "Apply this ₹"+m.sp_pay_amt.toLocaleString()+" charge to a tenant in one tap."}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {reqTen && <button onClick={() => showConfirm(`${t.chargeTo || "Charge"} ₹${m.sp_pay_amt.toLocaleString()} → ${reqTen.name}?`, () => chargeSpAmtTo(m, m.tid))} style={{
                  flex: "1 1 auto", padding: "8px 10px", borderRadius: 8, border: "none",
                  background: h.ac, color: "#fff", cursor: "pointer",
                  fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
                }}>
                  {`${t.chargeTo || "Charge"} ₹${m.sp_pay_amt.toLocaleString()} → ${reqTen.name}`}
                </button>}
                <button onClick={() => showConfirm(`${t.splitAcrossTenants || "Split"} ₹${m.sp_pay_amt.toLocaleString()}?`, () => chargeSpAmtTo(m, "all"))} style={{
                  flex: "1 1 auto", padding: "8px 10px", borderRadius: 8, border: `1px solid ${h.bd}`,
                  background: "transparent", color: h.tx, cursor: "pointer",
                  fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
                }}>
                  {t.splitAcrossTenants || "Split among all"}
                </button>
                <button onClick={() => startEdit(m)} style={{
                  flex: "0 0 auto", padding: "8px 10px", borderRadius: 8, border: `1px solid ${h.bd}`,
                  background: "transparent", color: h.sc, cursor: "pointer",
                  fontSize: 11, fontWeight: 600,
                }}>{t.custom || "Custom…"}</button>
              </div>
            </div>;
          })()}
        </div>}

        {m.st !== "done" && m.exp_amt > 0 && <div style={{ background: `${h.ac}08`, borderRadius: 8, padding: 8, marginTop: 8, border: `1px solid ${h.ac}30`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ color: h.ac, fontSize: 10, fontWeight: 600, textTransform: "uppercase", marginBottom: 2 }}>{t.expenseInfo}</div>
            <div style={{ color: h.tx, fontSize: 13, fontWeight: 700 }}>₹{m.exp_amt.toLocaleString()} <span style={{ color: h.sc, fontWeight: 400, fontSize: 11 }}>→ {m.exp_target === "all" ? t.allTenants : (tens.find(x => x.id === m.exp_target)?.name || "?")}</span></div>
          </div>
          {m.exp_applied ? <span style={{ ...bdg(`${h.gn}20`, h.gn), fontSize: 10 }}>{"✓ " + t.expAlreadyApplied}</span>
            : <button onClick={() => showConfirm(t.applyExp + "?", () => applyExpense(m))} style={{ padding: "6px 10px", borderRadius: 8, border: "none", background: h.ac, color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>{t.applyExp}</button>}
        </div>}

        {assignId === m.id && (() => {
          const mTenant = tens.find(te => te.id === m.tid);
          const mProp = props.find(p => p.id === mTenant?.pid) || props.find(p => p.tid === m.tid) || null;
          const ranked = rankServicePersons(sps, mProp, m.desc);
          const showManual = selSpId === "__manual__";
          // Why the list might be empty:
          //   - org has zero service_persons rows           → show "add one" CTA
          //   - rows exist but all have active = 0          → show "activate one" hint
          //   - rows exist + active but rank() filtered all → won't happen since
          //     rank doesn't filter by skill / distance
          const totalSps = (sps || []).length;
          const activeSps = (sps || []).filter(s => s.active).length;
          const showEmptyState = !showManual && ranked.length === 0;
          return <div style={{ background: `${h.wn}08`, borderRadius: rad.md, padding: spStyle.md, marginTop: 8, border: `1px solid ${h.wn}30` }}>
            <div style={{ color: h.wn, fontWeight: 600, fontSize: 12, marginBottom: spStyle.sm }}>{t.assignTo}</div>

            {/* Empty-state explanation — replaces the silent "no list, just
                Enter Manually" surface that left landlords wondering where
                the SP picker went. */}
            {showEmptyState && (
              <div style={{
                background: h.cd, borderRadius: rad.sm, padding: spStyle.md,
                border: `1px solid ${h.bd}`, marginBottom: spStyle.sm,
              }}>
                <div style={{ color: h.tx, fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                  {totalSps === 0
                    ? (t.noSpYet || "No service persons added yet")
                    : activeSps === 0
                      ? (t.noActiveSp || "All service persons are inactive")
                      : (t.noSuggestedSp || "No suggested service persons")}
                </div>
                <div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5, marginBottom: spStyle.sm }}>
                  {totalSps === 0
                    ? (t.addSpHint || "Add a plumber, electrician, or other tradesperson once and they'll appear in this list for every future job.")
                    : activeSps === 0
                      ? (t.activateSpHint || "Reactivate a service person from the Service Persons page to assign jobs to them.")
                      : (t.useManualHint || "Enter the worker's details manually below.")}
                </div>
                <button
                  onClick={() => { setAssignId(null); setAsnName(""); setAsnPhone(""); setSelSpId(""); go("servicePersons"); }}
                  style={{
                    width: "100%", padding: 10, borderRadius: rad.sm,
                    border: `1px solid ${h.ac}`, background: `${h.ac}10`,
                    color: h.ac, fontSize: 12, fontWeight: 600, cursor: "pointer",
                  }}>
                  {totalSps === 0
                    ? (t.addServicePerson || "+ Add a Service Person")
                    : (t.manageServicePersons || "Manage Service Persons")}
                </button>
              </div>
            )}

            {/* Smart Suggestions */}
            {ranked.length > 0 && !showManual && <div style={{ marginBottom: spStyle.md }}>
              <div style={{ color: h.ts, fontSize: 10, fontWeight: 600, textTransform: "uppercase", marginBottom: spStyle.xs }}>{t.suggestedPersons || "Suggested"}</div>
              {ranked.map(s => {
                const activeJobs = mts.filter(mt => mt.sp_id === s.id && mt.st !== "done").length;
                const selected = selSpId === s.id;
                return <div key={s.id} onClick={() => { setSelSpId(s.id); setAsnName(s.name); setAsnPhone(s.ph); }}
                  style={{ display: "flex", alignItems: "center", gap: spStyle.sm, padding: `${spStyle.sm}px ${spStyle.md}px`, borderRadius: rad.sm, border: `1px solid ${selected ? h.ac : h.bd}`, background: selected ? `${h.ac}08` : h.cd, marginBottom: spStyle.xs, cursor: "pointer", transition: "all 0.15s" }}>
                  <div style={icBox(s.matchScore >= 2 ? h.gn : h.ts, 32)}>
                    <Ic n="wrench" s={14} c={s.matchScore >= 2 ? h.gn : h.ts} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{s.name}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 2 }}>
                      <span style={{ ...bdg(`${h.ac}15`, h.ac), fontSize: 9 }}>{s.skill || "General"}</span>
                      {s.distance != null && <span style={{ ...bdg(`${h.gn}15`, h.gn), fontSize: 9 }}>{s.distance < 1 ? `${Math.round(s.distance * 1000)}m` : `${s.distance.toFixed(1)}km`}</span>}
                      {s.distance == null && <span style={{ ...bdg(`${h.tt}15`, h.tt), fontSize: 9 }}>{t.noLocation || "No location"}</span>}
                      {activeJobs > 0 && <span style={{ ...bdg(`${h.wn}15`, h.wn), fontSize: 9 }}>{activeJobs} {t.activeJobs || "active"}</span>}
                    </div>
                  </div>
                  {selected && <Ic n="check" s={16} c={h.ac} />}
                </div>;
              })}
            </div>}

            {/* Manual entry toggle */}
            {!showManual && <button onClick={() => { setSelSpId("__manual__"); setAsnName(""); setAsnPhone(""); }} style={{ display: "flex", alignItems: "center", gap: spStyle.xs, background: "none", border: `1px dashed ${h.bd}`, borderRadius: rad.sm, padding: `${spStyle.sm}px ${spStyle.md}px`, color: h.ts, fontSize: 12, cursor: "pointer", width: "100%", marginBottom: spStyle.sm }}>
              <Ic n="plus" s={14} c={h.ts} /> {t.manualEntry || "Enter manually"}
            </button>}

            {/* Manual entry fields */}
            {showManual && <>
              <Fld l={t.assigneeName} v={asnName} set={setAsnName} h={h} ph="e.g. Raj Plumber" />
              <Fld l={t.assigneePhone} v={asnPhone} set={setAsnPhone} h={h} ph="+91 98765..." />
            </>}

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => { doAssign(m); }} disabled={!asnName} style={{ flex: 1, padding: 8, borderRadius: 8, border: "none", background: asnName ? h.wn : h.ib, color: asnName ? "#fff" : h.tt, cursor: asnName ? "pointer" : "default", fontSize: 12, fontWeight: 600 }}>{t.assign}</button>
              <button onClick={() => { setAssignId(null); setAsnName(""); setAsnPhone(""); setSelSpId(""); }} style={{ flex: 1, padding: 8, borderRadius: 8, border: `1px solid ${h.bd}`, background: "transparent", color: h.sc, cursor: "pointer", fontSize: 12 }}>{t.cancel}</button>
            </div>
          </div>;
        })()}

        {resolveId === m.id && (() => {
          // Form is only submittable once the landlord has entered a real
          // expense amount AND picked a charge target. "None" is the
          // initial sentinel so they can't accidentally fast-click Done
          // and hit "All Tenants" by default.
          const expNum = Number(resExpAmt) || 0;
          // No expense → resolvable as-is (zero-cost / landlord-absorbed).
          // An expense needs a charge target before Done unlocks.
          const canResolve = expNum > 0 ? (!!resExpTarget && resExpTarget !== "none") : true;
          return <div style={{ background: `${h.gn}08`, borderRadius: 10, padding: 12, marginTop: 8, border: `1px solid ${h.gn}30` }}>
            <div style={{ color: h.gn, fontWeight: 600, fontSize: 12, marginBottom: 8 }}>{t.done} — {t.resolve}</div>
            <Fld l={t.assigneeName} v={resAsnN} set={setResAsnN} h={h} ph="e.g. Raj Plumber" />
            <Fld l={t.assigneePhone} v={resAsnP} set={setResAsnP} h={h} ph="+91 98765..." />
            <Fld l={(t.expAmt || "Expense (₹)") + " (optional)"} v={resExpAmt} set={setResExpAmt} h={h} type="number" ph="₹0" />
            <label style={{ color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6 }}>{t.expTarget}</label>
            <select value={resExpTarget} onChange={e => setResExpTarget(e.target.value)} style={{ ...inpS(h), marginBottom: 8 }}>
              <option value="none">— {t.selectChargeTarget || "Select who to charge"} —</option>
              <option value="all">{t.allTenants}</option>
              {occTenants.map(te => <option key={te.id} value={te.id}>{te.name}</option>)}
            </select>
            <Fld l={t.resNotes} v={resNotes} set={setResNotes} h={h} ph={t.whatWasDone} />
            {/* Inline validation hint — only when the form is actively open
                and either field is missing. Keeps the surface friendly when
                the user has filled everything correctly. */}
            {!canResolve && (
              <div style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "8px 10px", marginBottom: 10,
                background: `${h.wn || "#d97706"}10`,
                borderRadius: 8, border: `1px solid ${h.wn || "#d97706"}30`,
              }}>
                <span style={{ fontSize: 13 }}>ℹ️</span>
                <span style={{ color: h.wn || "#d97706", fontSize: 11, fontWeight: 500 }}>
                  {"Choose who to charge for this expense — or clear the amount if you're absorbing the cost."}
                </span>
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => doResolve(m)}
                disabled={!canResolve}
                style={{
                  flex: 1, padding: 8, borderRadius: 8, border: "none",
                  background: canResolve ? h.gn : (h.ib || "#f4f4f8"),
                  color: canResolve ? "#fff" : (h.tt || h.sc),
                  cursor: canResolve ? "pointer" : "not-allowed",
                  fontSize: 12, fontWeight: 600,
                  opacity: canResolve ? 1 : 0.7,
                }}>{t.done}</button>
              <button onClick={() => { setResolveId(null); setResNotes(""); setResAsnN(""); setResAsnP(""); setResExpAmt(""); setResExpTarget("none"); }} style={{ flex: 1, padding: 8, borderRadius: 8, border: `1px solid ${h.bd}`, background: "transparent", color: h.sc, cursor: "pointer", fontSize: 12 }}>{t.cancel}</button>
            </div>
          </div>;
        })()}

        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <button onClick={() => startEdit(m)} style={{ flex: 1, padding: 8, borderRadius: 8, border: "none", background: `${h.ac}15`, color: h.ac, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{"✏️ " + t.edit}</button>
          {m.st !== "done" && <>{m.st === "open" && <button onClick={() => {
            setAssignId(m.id); setAsnName(""); setAsnPhone(""); setResolveId(null);
          }} style={{ flex: 1, padding: 8, borderRadius: 8, border: "none", background: `${h.wn}15`, color: h.wn, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.assign}</button>}
            <button onClick={() => {
              setResolveId(m.id); setResAsnN(m.asn_n || ""); setResAsnP(m.asn_p || ""); setResExpAmt(m.exp_amt || ""); setResExpTarget(m.exp_target || "none"); setResNotes(""); setAssignId(null);
            }} style={{ flex: 1, padding: 8, borderRadius: 8, border: "none", background: `${h.gn}15`, color: h.gn, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.done}</button></>}
          <button onClick={() => deleteMaint(m)} style={{ flex: 1, padding: 8, borderRadius: 8, border: "none", background: `${h.rd}15`, color: h.rd, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{"✕ " + t.delete}</button>
        </div>
      </div>;
    })}
    </div>
  </div>;
}

export default MaintPage;
